import {
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  Container,
  Header,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { createItem, fetchItem, fetchItems, updateItem } from '../api/generic';
import { useAuthContext } from '../contexts/AuthContext';
import { prefixCollection } from '../utils/prefix';

const types: SelectProps.Option[] = [
  { label: 'Batch', value: 'batches' },
  { label: 'Invoice', value: 'invoice' },
  { label: 'Inventory Item', value: 'inventoryItem' },
  { label: 'Recipe', value: 'recipes' },
];

const Data = () => {
  const { tenant } = useAuthContext();
  const [selectedType, setSelectedType] = useState<SelectProps.Option | null>(null);
  const [documentId, setDocumentId] = useState('');
  const [jsonContent, setJsonContent] = useState('');

  const [debouncedId, setDebouncedId] = useState(documentId);

  const [itemOptions, setItemOptions] = useState<SelectProps.Option[]>([]);
  const [tenantOptions, setTenantOptions] = useState<ButtonDropdownProps.ItemOrGroup[]>([]);

  useDebounce(
    () => {
      setDebouncedId(documentId);
    },
    500,
    [documentId],
  );

  useEffect(() => {
    // Fetch all tenants to populate the tenant dropdown
    fetchItems('tenant').then((items: any[]) => {
      setTenantOptions(
        items.map((item) => ({
          id: item.name,
          text: item.companyName || item.name || item.id,
        })),
      );
    });
  }, []);

  useEffect(() => {
    if (selectedType?.value && debouncedId) {
      fetchItem(prefixCollection(selectedType.value, tenant), debouncedId).then((item) =>
        setJsonContent(JSON.stringify(item, null, 2)),
      );
      // Fetch logic can go here
    }
  }, [debouncedId, selectedType]);

  useEffect(() => {
    setJsonContent('');
    setDocumentId('');

    if (selectedType?.value) {
      // Fetch all items of this type to populate the dropdown
      fetchItems(prefixCollection(selectedType.value, tenant)).then((items: any[]) => {
        setItemOptions(
          items.map((item) => ({
            label:
              selectedType.value === 'batches'
                ? `${item.batchId} - ${item.name}`
                : item['name'] || item['title'] || item.id,
            value: item.id,
          })),
        );
      });
    } else {
      setItemOptions([]);
    }
  }, [selectedType]);

  const handleCreateUpdate = async () => {
    if (documentId && selectedType?.value) {
      await updateItem(prefixCollection(selectedType.value, tenant), documentId, JSON.parse(jsonContent));
    } else if (selectedType?.value) {
      await createItem(prefixCollection(selectedType.value, tenant), JSON.parse(jsonContent));
    }
    setDocumentId('');
    setJsonContent('');
  };

  const handleMoveToTenant = async (tenant: string) => {
    if (documentId && selectedType?.value) {
      const data = await fetchItem(prefixCollection(selectedType.value, tenant), documentId);
      await createItem(prefixCollection(selectedType.value, tenant as string), JSON.parse(jsonContent));
      // Optionally delete the item from the current tenant after moving
      // await deleteItem(prefixCollection(selectedType.value, tenant), documentId);
      setDocumentId('');
      setJsonContent('');
    }
  };

  return (
    <Container header={<Header variant="h2">Hack Panel</Header>}>
      <SpaceBetween size="m">
        <Select
          selectedOption={selectedType}
          onChange={({ detail }) => setSelectedType(detail.selectedOption)}
          options={types}
          placeholder="Select a type"
        />

        {selectedType && (
          <>
            <Select
              selectedOption={itemOptions.find((option) => option.value === documentId) || null}
              onChange={({ detail }) => setDocumentId(detail.selectedOption?.value ?? '')}
              options={itemOptions}
              placeholder="Select existing document to load"
              selectedAriaLabel="Selected"
              filteringType="auto"
            />

            <Input
              placeholder="Enter document ID"
              value={documentId}
              onChange={({ detail }) => setDocumentId(detail.value)}
            />

            <Textarea
              placeholder='Enter JSON data (e.g. { "foo": "bar" })'
              value={jsonContent}
              onChange={({ detail }) => setJsonContent(detail.value)}
              rows={10}
            />
          </>
        )}
        <SpaceBetween size="m" direction="horizontal">
          <Button onClick={handleCreateUpdate}>{documentId ? 'Update' : 'Create'}</Button>
          {documentId && (
            <ButtonDropdown onItemClick={({ detail }) => handleMoveToTenant(detail.id)} items={tenantOptions}>
              Copy to
            </ButtonDropdown>
          )}
          {documentId ? <Box variant="awsui-gen-ai-label">OBS! This does not delete the item!</Box> : ''}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

export default Data;
